// Organisations partenaires (spec § 3) : compte connecté, son organisation et le pipeline à appliquer.
import type { ReactNode } from "react";
import { useContext, useMemo } from "react";
import { useGetIdentity, useGetList, useGetOne, useStore } from "ra-core";

import {
  CONFIGURATION_STORE_KEY,
  ConfigurationOverrideContext,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import type { Deal, PartnerOrg, Sale } from "../types";
import { seesOrganisation } from "./accountKind";
import { pipelineForDeal } from "./partnerships";

export const usePartnerOrg = () => {
  const { identity } = useGetIdentity();
  const { data: sale, isPending: salePending } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: identity?.id != null },
  );
  const orgId = sale?.partner && !sale.administrator ? sale.org_id : null;
  const { data: org, isPending: orgPending } = useGetOne<PartnerOrg>(
    "partner_orgs",
    { id: orgId ?? 0 },
    { enabled: orgId != null },
  );
  const partner = !!sale?.partner && !sale.administrator;
  const subscriber = partner && sale?.partner_offer === "subscriber";
  return {
    sale,
    org: orgId != null ? org : undefined,
    partner,
    subscriber,
    // Administrateur d'une organisation abonnée : gère ses membres et son pipeline.
    orgAdmin: subscriber && !!sale?.org_admin && orgId != null,
    // « Mon organisation » (menu, paramètres du téléphone, page) : abonnés seulement.
    showOrg: seesOrganisation(sale) && !!org,
    isPending:
      identity?.id == null || salePending || (orgId != null && orgPending),
  };
};

/** Organisations lisibles par le compte (la sienne et celles qui lui transmettent des affaires). */
export const useReadableOrgs = () => {
  const { data } = useGetList<PartnerOrg>("partner_orgs", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  return data;
};

/**
 * Pipeline d'une affaire : celui de l'organisation propriétaire (`pipelineForDeal`), celui du client pour l'équipe ou
 * un hôte illisible (lu dans la configuration elle-même, pas dans une surcharge d'onglet). Sans affaire (création),
 * celui de l'écran en cours.
 */
export const useDealPipeline = (deal?: Pick<Deal, "org_id"> | null) => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  const override = useContext(ConfigurationOverrideContext);
  const { org } = usePartnerOrg();
  const orgs = useReadableOrgs();
  const client = {
    dealStages: config.dealStages ?? defaultConfiguration.dealStages,
    dealClosedStages:
      config.dealClosedStages ?? defaultConfiguration.dealClosedStages,
  };
  if (!deal) return override ?? client;
  return pipelineForDeal(deal, org, orgs ?? [], client);
};

/** Remplace les étapes du client par celles de l'organisation pour tout ce qui est rendu dessous. */
export const OrgPipeline = ({
  org,
  children,
}: {
  org: PartnerOrg | undefined;
  children: ReactNode;
}) => {
  const value = useMemo(
    () =>
      org
        ? {
            dealStages: org.deal_stages,
            dealClosedStages: org.deal_closed_stages,
          }
        : null,
    [org],
  );
  return (
    <ConfigurationOverrideContext.Provider value={value}>
      {children}
    </ConfigurationOverrideContext.Provider>
  );
};
